<script setup lang="ts">
import {
  BeamQualityContainerDto,
  BeamShapingContainerDto,
  ISingleSystemSetupParameter,
  MedicalSystemSetupBaseDto,
  type TaskTypeDescription,
} from '@/api/generator';
import { computed, ref, toRef } from 'vue';
import { useFieldValue, useSetFieldValue } from 'vee-validate';
import {
  instanceOfSingleSetupParameterWithGeometry,
  SetupParameterOverviewFilterObject,
} from './types';
import { getSetupParameterName, behavesLikeManualTask } from '@/views/tasks/utils.ts';

import Dialog from '@/components/Dialog/Dialog.vue';
import SetupParameterOverviewFilter from './SetupParameterOverviewFilter.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';

import {
  createEntriesForBeamQualityContainer,
  createEntriesForBeamShapingContainer,
  createEntryForSingleSetupParameter,
  SelectableSetupParameterTableEntry,
  updateSelection,
  isSelectableSetupParameterTableEntry,
  SetupParameterTableEntry,
} from './SetupParameterSelectDialogUtil';

import { useTranslation } from 'i18next-vue';
import { translatedEntityNameIncludes } from '@/utils/helper';

const { t } = useTranslation();

const props = defineProps<{
  modelValue: boolean; // open state of dialog
  name: string; // taskAreas[{areaIndex}].medicalSystemSetup
  taskType: TaskTypeDescription;
}>();

const emits = defineEmits<{
  'update:modelValue': [open: boolean];
  update: [medicalSystemSetup: MedicalSystemSetupBaseDto];
}>();

const medicalSystemSetupPath = toRef(() => props.name);
const medicalSystemSetup = useFieldValue<MedicalSystemSetupBaseDto>(medicalSystemSetupPath.value);
const setMedicalSystemSetup = useSetFieldValue<MedicalSystemSetupBaseDto>(
  medicalSystemSetupPath.value,
);

const singleSetupParametersPath = toRef(() => `${props.name}.singleSetupParameters`);
const singleSetupParameters = useFieldValue<ISingleSystemSetupParameter[]>(
  singleSetupParametersPath.value,
);

const beamQualityContainerPath = toRef(() => `${props.name}.beamQualityContainer`);
const beamQualityContainer = useFieldValue<BeamQualityContainerDto | undefined>(
  beamQualityContainerPath.value,
);

const beamShapingContainerPath = toRef(() => `${props.name}.beamShapingContainer`);
const beamShapingContainer = useFieldValue<BeamShapingContainerDto | undefined>(
  beamShapingContainerPath.value,
);

function paramKeyWordSearch(entry: SetupParameterTableEntry) {
  function findInNames(parameter?: ISingleSystemSetupParameter) {
    return translatedEntityNameIncludes(parameter, filterObject.value.search);
  }

  function findInGeometries(parameter?: ISingleSystemSetupParameter) {
    return (
      instanceOfSingleSetupParameterWithGeometry(parameter) &&
      translatedEntityNameIncludes(parameter?.geometry, filterObject.value.search)
    );
  }

  return findInNames(entry.parameter) || findInGeometries(entry.parameter);
}

const singleSetupParameterEntries = computed(() => {
  return singleSetupParameters.value
    .map((parameter, index) =>
      createEntryForSingleSetupParameter(parameter, {
        path: `singleSetupParameters[${index}]`,
        itemType: 'Single',
      }),
    )
    .filter(paramKeyWordSearch);
});

const containerEntries = computed(() => {
  const beamQualityContainerEntries = beamQualityContainer.value
    ? createEntriesForBeamQualityContainer(beamQualityContainer.value, {
        path: 'beamQualityContainer',
      })
    : [];

  const beamShapingContainerEntries = beamShapingContainer.value
    ? createEntriesForBeamShapingContainer(beamShapingContainer.value, {
        path: 'beamShapingContainer',
      })
    : [];

  return [beamQualityContainerEntries, beamShapingContainerEntries]
    .filter((group) => group.some(paramKeyWordSearch))
    .flat();
});

const filterObject = ref<SetupParameterOverviewFilterObject>({});

const headers = [
  {
    title: '',
    key: 'checkbox',
    width: 30,
    sortable: false,
  },
  {
    title: '',
    key: 'nameAndID',
    width: 500,
    sortable: false,
  },
  {
    title: t('Unit_one'),
    key: 'unitAbbreviation',
    width: 50,
    sortable: false,
  },
] as const;

function toggleSelection(entry: SelectableSetupParameterTableEntry) {
  if (!entry.isRequired) {
    entry.selected.value = !entry.selected.value;
  }
}

function toggleDialog(open = props.modelValue) {
  emits('update:modelValue', open);
}

async function apply() {
  setMedicalSystemSetup(
    await updateSelection(
      [...singleSetupParameterEntries.value, ...containerEntries.value],
      medicalSystemSetup.value,
      props.taskType,
    ),
  );
  toggleDialog(false);
}
</script>

<template>
  <Dialog :modelValue="modelValue" @update:modelValue="toggleDialog">
    <template #title>
      <div class="d-flex flex-wrap gc-8">
        <span class="flex-1-0-100">{{ $t('AddSetupParameter') }}</span>
        <SetupParameterOverviewFilter v-model="filterObject" class="mb-1" />

        <InlineNotification
          class="flex-0-0 align-self-end mb-3"
          :value="{
            type: 'info',
            message: $t('MissingSetupParametersHint'),
          }"
        />
      </div>
    </template>

    <template #text>
      <v-row style="height: 60vh">
        <v-col
          v-for="(items, index) in [singleSetupParameterEntries, containerEntries]"
          :key="index"
        >
          <OverviewTable
            :headers="headers"
            :showItemsCount="false"
            data-testid="DataTable_ContainerSetupParameter"
            :zebra="true"
            :noSpacing="true"
            :items="items"
            @click:row="
              (_event, { item: entry }) => {
                toggleSelection(entry as unknown as SelectableSetupParameterTableEntry);
              }
            "
          >
            <template #header.nameAndID="headerProps">
              <OverviewTableHeader v-bind="headerProps">
                {{ $t('Name') }}
                <span class="font-weight-medium">| {{ $t('ID') }}</span>
              </OverviewTableHeader>
            </template>

            <template #item.checkbox="{ item }">
              <Checkbox
                v-if="isSelectableSetupParameterTableEntry(item)"
                v-model="item.selected.value"
                :disabled="!behavesLikeManualTask(taskType) && item.isRequired"
                hideDetails
                class="mr-4"
              />
            </template>

            <template #item.nameAndID="{ item }">
              <div v-if="item.itemType === 'ContainerHeader' && item.name">
                {{ $t(item.name) }}
              </div>

              <div v-else>
                <span class="text-body-1 font-weight-bold">
                  {{ getSetupParameterName(item.parameter) }}
                </span>
                <span> | {{ item.iuid }}</span>
              </div>
            </template>

            <template #item.unitAbbreviation="{ item }">
              <div>
                {{ item.unit }}
              </div>
            </template>
          </OverviewTable>
        </v-col>
      </v-row>
    </template>

    <template #actions>
      <TextVBtn :text="$t('Cancel')" @click="toggleDialog(false)" />

      <SecondaryVBtn @click="apply()">
        {{ $t('Apply') }}
      </SecondaryVBtn>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:deep(.v-selection-control) {
  min-height: unset;
}
</style>
